import React from 'react';
import { View, Text, Pressable, Platform, type ImageSourcePropType } from 'react-native';
import { Image } from 'expo-image';
import {
  BookOpen,
  Globe,
  HelpCircle,
  Medal,
  Shield,
  ChevronDown,
  ChevronRight,
  Shirt,
  Ticket,
  Trophy,
  Volume2,
  VolumeX,
  Zap,
} from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../src/shared/helpers/imageWebAlt.helper';
import { SoundPressable } from '../../src/shared/components/SoundPressable/SoundPressable';
import { SOUND_IDS } from '../../src/shared/constants/sounds.constants';
import {
  GLOBAL_MENU_COPY,
  GLOBAL_MENU_REDEEM_COUPON_LABEL,
  GLOBAL_MENU_SOUND_EFFECTS_LABEL,
  getGlobalMenuWorldCupRulesLabel,
  GLOBAL_MENU_AVATAR_LOOK_PREVIEW_LABEL,
  GLOBAL_MENU_PRIVACY_AND_DATA_LABEL,
  GLOBAL_MENU_OFFICIAL_WEBSITE_LABEL,
  GLOBAL_MENU_SUPPORT_LABEL,
} from './globalMenuModal.constants';
import { LEGAL_ROWS } from '../../src/privacy/privacyAndData.constants';
import {
  getLegalDocumentVariantForSlug,
  isInAppLegalDocumentVariant,
} from '../../src/landing/helpers/legalRootSlug.helper';
import { PRIVACY_AND_DATA_SCREEN } from '../../src/privacy/privacyAndData.constants';
import { LanguageContext } from '../../src/i18n';
import { useMenuStore } from '../../src/shared/stores/menuStore';
import { useCelebrationStore } from '../../src/economy/stores/celebrationStore';
import type { getGlobalMenuModalStyles } from './GlobalMenuModal.styles';
import type { Theme } from '../../src/theme/getTheme';
import type { InAppLegalDocumentVariant } from '../../src/landing/types/legalDocument.types';

const LEGAL_ROW_LABEL_KEYS = {
  privacy: 'privacyDocumentTitle',
  dataDeletion: 'dataDeletionDocumentTitle',
  terms: 'termsDocumentTitle',
  credits: 'creditsDocumentTitle',
} as const;

export interface MenuItemsListProps {
  styles: ReturnType<typeof getGlobalMenuModalStyles>;
  theme: Theme;
  showWebPressableTransition: boolean;
  testID?: string;
  isPrivacySubmenuExpanded: boolean;
  isMuted: boolean;
  onToggleSoundMute: () => void;
  onOpenDailyReward: () => void;
  onOpenCouponRedeem: () => void;
  onOpenTutorial: () => void;
  onOpenWorldCupRules: () => void;
  onOpenAvatarCustomize: () => void;
  onTogglePrivacyMenu: () => void;
  onSelectLegalRow: (variant: InAppLegalDocumentVariant) => void;
  onOpenAccountDeletion: () => void;
  onOpenDataControls: () => void;
  onOpenOfficialWebsite: () => void;
  onOpenSupport: () => void;
  coinIconSource: ImageSourcePropType;
}

export const MenuItemsList: React.FC<MenuItemsListProps> = ({
  styles,
  theme,
  showWebPressableTransition,
  testID,
  isPrivacySubmenuExpanded,
  isMuted,
  onToggleSoundMute,
  onOpenDailyReward,
  onOpenCouponRedeem,
  onOpenTutorial,
  onOpenWorldCupRules,
  onOpenAvatarCustomize,
  onTogglePrivacyMenu,
  onSelectLegalRow,
  onOpenAccountDeletion,
  onOpenDataControls,
  onOpenOfficialWebsite,
  onOpenSupport,
  coinIconSource,
}) => {
  const languageContext = React.useContext(LanguageContext);
  if (!languageContext) {
    throw new Error('MenuItemsList must be used within LanguageProvider');
  }
  const { t } = languageContext;
  const iconColor = theme.gray?.[600] ?? theme.gray?.[700] ?? '#71717a';
  const menuItemStyle = ({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
    styles.menuItem,
    showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
    pressed && styles.menuItemPressed,
    hovered && Platform.OS === 'web' && styles.menuItemHover,
  ];

  return (
    <View style={styles.menuContent}>
      <Pressable
        style={menuItemStyle}
        onPress={onOpenDailyReward}
        accessibilityRole="button"
        accessibilityLabel="Collect daily coins"
        testID={testID ? `${testID}-daily-reward-menu-item` : 'global-menu-modal-daily-reward-menu-item'}
      >
        <Image source={coinIconSource} style={styles.menuItemCoinIcon} alt={WEB_IMAGE_ALT_DECORATIVE} />
        <Text style={styles.menuItemText}>Daily Reward</Text>
      </Pressable>

      <SoundPressable
        tapSoundId={SOUND_IDS.UI_TAP_NAV}
        style={menuItemStyle}
        onPress={onToggleSoundMute}
        accessibilityRole="switch"
        accessibilityState={{ checked: !isMuted }}
        accessibilityLabel={`${GLOBAL_MENU_SOUND_EFFECTS_LABEL}: ${isMuted ? GLOBAL_MENU_COPY.SOUND_EFFECTS_MUTED : GLOBAL_MENU_COPY.SOUND_EFFECTS_ON}`}
        testID={testID ? `${testID}-sound-toggle` : 'global-menu-modal-sound-toggle'}
      >
        {isMuted ? (
          <VolumeX size={22} color={iconColor} style={styles.menuItemIconLeft} />
        ) : (
          <Volume2 size={22} color={iconColor} style={styles.menuItemIconLeft} />
        )}
        <Text style={[styles.menuItemText, styles.menuItemTextFlex]}>{GLOBAL_MENU_SOUND_EFFECTS_LABEL}</Text>
        <Text style={styles.menuItemSoundStatusText}>
          {isMuted ? GLOBAL_MENU_COPY.SOUND_EFFECTS_MUTED : GLOBAL_MENU_COPY.SOUND_EFFECTS_ON}
        </Text>
      </SoundPressable>

      <Pressable
        style={menuItemStyle}
        onPress={onOpenCouponRedeem}
        accessibilityRole="button"
        accessibilityLabel={GLOBAL_MENU_REDEEM_COUPON_LABEL}
        testID={testID ? `${testID}-redeem-coupon-menu-item` : 'global-menu-modal-redeem-coupon-menu-item'}
      >
        <Ticket size={22} color={iconColor} style={styles.menuItemIconLeft} />
        <Text style={styles.menuItemText}>{GLOBAL_MENU_REDEEM_COUPON_LABEL}</Text>
      </Pressable>

      <Pressable
        style={menuItemStyle}
        onPress={onOpenTutorial}
        testID={testID ? `${testID}-tutorial-button` : 'global-menu-modal-tutorial-button'}
      >
        <BookOpen size={22} color={iconColor} style={styles.menuItemIconLeft} />
        <Text style={styles.menuItemText}>Tutorial</Text>
      </Pressable>

      <Pressable
        style={menuItemStyle}
        onPress={onOpenAvatarCustomize}
        accessibilityRole="button"
        accessibilityLabel={GLOBAL_MENU_AVATAR_LOOK_PREVIEW_LABEL}
        testID={testID ? `${testID}-avatar-customize-menu-item` : 'global-menu-modal-avatar-customize-menu-item'}
      >
        <Shirt size={22} color={iconColor} style={styles.menuItemIconLeft} />
        <Text style={styles.menuItemText}>{GLOBAL_MENU_AVATAR_LOOK_PREVIEW_LABEL}</Text>
      </Pressable>

      <Pressable
        style={({ pressed, hovered }) => [
          styles.menuItem,
          showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.menuItemPressed,
          hovered && Platform.OS === 'web' && styles.menuItemHover,
        ]}
        onPress={onOpenWorldCupRules}
        accessibilityRole="button"
        accessibilityLabel={getGlobalMenuWorldCupRulesLabel()}
        testID={testID ? `${testID}-world-cup-rules` : 'global-menu-modal-world-cup-rules'}
      >
        <Trophy size={22} color={iconColor} style={styles.menuItemWorldCupIcon} strokeWidth={2.2} />
        <Text style={styles.menuItemText}>{getGlobalMenuWorldCupRulesLabel()}</Text>
      </Pressable>

      <View>
        <Pressable
          style={menuItemStyle}
          onPress={onTogglePrivacyMenu}
          accessibilityRole="button"
          accessibilityLabel={GLOBAL_MENU_PRIVACY_AND_DATA_LABEL}
          accessibilityState={{ expanded: isPrivacySubmenuExpanded }}
          testID={testID ? `${testID}-privacy-and-data` : 'global-menu-modal-privacy-and-data'}
        >
          <Shield size={22} color={iconColor} style={styles.menuItemIconLeft} />
          <Text style={[styles.menuItemText, styles.menuItemTextFlex]}>{GLOBAL_MENU_PRIVACY_AND_DATA_LABEL}</Text>
          {isPrivacySubmenuExpanded ? (
            <ChevronDown size={20} color={theme.gray?.[500] ?? '#71717a'} style={styles.menuPrivacyChevron} />
          ) : (
            <ChevronRight size={20} color={theme.gray?.[500] ?? '#71717a'} style={styles.menuPrivacyChevron} />
          )}
        </Pressable>

        {isPrivacySubmenuExpanded
          ? LEGAL_ROWS.map((row) => {
              const variant = getLegalDocumentVariantForSlug(row.slug);
              if (!variant || !isInAppLegalDocumentVariant(variant)) return null;
              const legalLabelKey = LEGAL_ROW_LABEL_KEYS[row.key as keyof typeof LEGAL_ROW_LABEL_KEYS];
              const legalLabel = legalLabelKey ? t.legal[legalLabelKey] : row.key;
              return (
                <Pressable
                  key={row.key}
                  style={({ pressed, hovered }) => [
                    styles.menuPrivacySubItem,
                    showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.menuPrivacySubItemPressed,
                    hovered && Platform.OS === 'web' && styles.menuPrivacySubItemHover,
                  ]}
                  onPress={() => onSelectLegalRow(variant)}
                  accessibilityRole="button"
                  accessibilityLabel={legalLabel}
                  testID={testID ? `${testID}-privacy-sub-${row.key}` : `global-menu-modal-privacy-sub-${row.key}`}
                >
                  <Text style={styles.menuPrivacySubItemText}>{legalLabel}</Text>
                </Pressable>
              );
            })
          : null}

        {isPrivacySubmenuExpanded ? (
          <Pressable
            style={({ pressed, hovered }) => [
              styles.menuPrivacySubItem,
              showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.menuPrivacySubItemPressed,
              hovered && Platform.OS === 'web' && styles.menuPrivacySubItemHover,
            ]}
            onPress={onOpenDataControls}
            accessibilityRole="button"
            accessibilityLabel={t.footer.privacyControls}
            testID={testID ? `${testID}-privacy-data-controls` : 'global-menu-modal-privacy-data-controls'}
          >
            <Text style={styles.menuPrivacySubItemText}>{t.footer.privacyControls}</Text>
          </Pressable>
        ) : null}

        {isPrivacySubmenuExpanded ? (
          <Pressable
            style={({ pressed, hovered }) => [
              styles.menuPrivacySubItem,
              showWebPressableTransition && Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.menuPrivacySubItemPressed,
              hovered && Platform.OS === 'web' && styles.menuPrivacySubItemHover,
            ]}
            onPress={onOpenAccountDeletion}
            accessibilityRole="button"
            accessibilityLabel={PRIVACY_AND_DATA_SCREEN.DANGER_CARD_TITLE}
            testID={testID ? `${testID}-privacy-account-deletion` : 'global-menu-modal-privacy-account-deletion'}
          >
            <Text style={styles.menuPrivacySubItemDangerText}>{PRIVACY_AND_DATA_SCREEN.DANGER_CARD_TITLE}</Text>
          </Pressable>
        ) : null}
      </View>

      <Pressable
        style={menuItemStyle}
        onPress={onOpenSupport}
        accessibilityRole="button"
        accessibilityLabel={GLOBAL_MENU_SUPPORT_LABEL}
        testID={testID ? `${testID}-support-menu-item` : 'global-menu-modal-support-menu-item'}
      >
        <HelpCircle size={22} color={iconColor} style={styles.menuItemIconLeft} />
        <Text style={styles.menuItemText}>{GLOBAL_MENU_SUPPORT_LABEL}</Text>
      </Pressable>

      <Pressable
        style={menuItemStyle}
        onPress={onOpenOfficialWebsite}
        accessibilityRole="link"
        accessibilityLabel={GLOBAL_MENU_OFFICIAL_WEBSITE_LABEL}
        testID={testID ? `${testID}-official-website` : 'global-menu-modal-official-website'}
      >
        <Globe size={22} color={iconColor} style={styles.menuItemIconLeft} />
        <Text style={styles.menuItemText}>{GLOBAL_MENU_OFFICIAL_WEBSITE_LABEL}</Text>
      </Pressable>

      {__DEV__ ? (
        <>
          <Pressable
            style={menuItemStyle}
            onPress={() => {
              useMenuStore.getState().setMenuVisible(false);
              useCelebrationStore.getState().showLevelUp({ level: 47, xpCurrent: 115, xpTarget: 725 });
            }}
            accessibilityRole="button"
            accessibilityLabel="Preview level up"
            testID={testID ? `${testID}-dev-level-up` : 'global-menu-modal-dev-level-up'}
          >
            <Zap size={22} color={iconColor} style={styles.menuItemIconLeft} />
            <Text style={styles.menuItemText}>Preview level up</Text>
          </Pressable>
          <Pressable
            style={menuItemStyle}
            onPress={() => {
              useMenuStore.getState().setMenuVisible(false);
              useCelebrationStore
                .getState()
                .showRankUp({ fromRank: 4, toRank: 5, level: 41, xpCurrent: 8000, xpTarget: 15000 });
            }}
            accessibilityRole="button"
            accessibilityLabel="Preview rank up"
            testID={testID ? `${testID}-dev-rank-up` : 'global-menu-modal-dev-rank-up'}
          >
            <Medal size={22} color={iconColor} style={styles.menuItemIconLeft} />
            <Text style={styles.menuItemText}>Preview rank up</Text>
          </Pressable>
        </>
      ) : null}
    </View>
  );
};
